import React from 'react';
import { Link } from 'react-router-dom';
import { Factory, Zap, Droplets, GitBranch, Waves, Beaker, Cog } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const AllProducts = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Our Product Range',
      subtitle: 'Comprehensive industrial products and engineering services for global markets'
    },
    vi: {
      title: 'Dòng sản phẩm của chúng tôi',
      subtitle: 'Sản phẩm công nghiệp và dịch vụ kỹ thuật toàn diện cho thị trường toàn cầu'
    }
  };

  const t = content[language];

  const productCategories = [
    {
      name: language === 'en' ? 'Industrial Machinery' : 'Máy móc công nghiệp',
      icon: Factory,
      description: language === 'en' ? 'Heavy machinery and industrial equipment for manufacturing and processing' : 'Máy móc hạng nặng và thiết bị công nghiệp cho sản xuất và chế biến',
      color: '#264f78'
    },
    {
      name: language === 'en' ? 'MEP Equipment' : 'Thiết bị MEP',
      icon: Zap,
      description: language === 'en' ? 'Mechanical, electrical & plumbing systems for buildings and facilities' : 'Hệ thống cơ khí, điện và ống nước cho tòa nhà và cơ sở',
      color: '#2563eb'
    },
    {
      name: language === 'en' ? 'Water Pumps' : 'Máy bơm nước',
      icon: Droplets,
      description: language === 'en' ? 'Industrial and commercial water pumps for various applications' : 'Máy bơm nước công nghiệp và thương mại cho nhiều ứng dụng',
      color: '#0891b2'
    },
    {
      name: language === 'en' ? 'Pipe Fittings & Valves' : 'Phụ kiện ống & van',
      icon: GitBranch,
      description: language === 'en' ? 'HDPE, PVC, and stainless steel fittings for piping systems' : 'Phụ kiện HDPE, PVC và thép không gỉ cho hệ thống ống',
      color: '#6366f1'
    },
    {
      name: language === 'en' ? 'Pool Systems & Spares' : 'Hệ thống hồ bơi & phụ tùng',
      icon: Waves,
      description: language === 'en' ? 'Swimming pool equipment, accessories, and maintenance systems' : 'Thiết bị hồ bơi, phụ kiện và hệ thống bảo trì',
      color: '#06b6d4'
    },
    {
      name: language === 'en' ? 'Water Treatment Chemicals' : 'Hóa chất xử lý nước',
      icon: Beaker,
      description: language === 'en' ? 'Pool and industrial water treatment solutions with SDS' : 'Giải pháp xử lý nước hồ bơi và công nghiệp với SDS',
      color: '#8b5cf6'
    },
    {
      name: language === 'en' ? 'Engineering Spares' : 'Phụ tùng kỹ thuật',
      icon: Cog,
      description: language === 'en' ? 'OEM and aftermarket spare parts for industrial equipment' : 'Phụ tùng OEM và phụ tùng thay thế cho thiết bị công nghiệp',
      color: '#ec4899'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#264f78] to-[#1a3a57] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-lg text-white/90">{t.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#264f78] group cursor-pointer">
                  <div className="flex flex-col items-center text-center">
                    <div 
                      className="w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <Icon 
                        className="w-12 h-12 transition-colors duration-300" 
                        style={{ color: category.color }}
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#264f78] transition-colors duration-300">
                      {category.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Need a Quote?' : 'Cần báo giá?'}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {language === 'en'
              ? 'Contact us for competitive pricing and detailed product specifications'
              : 'Liên hệ với chúng tôi để biết giá cạnh tranh và thông số kỹ thuật sản phẩm chi tiết'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/request-quote"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#264f78] hover:bg-[#1a3a57] transition-all duration-300"
            >
              {language === 'en' ? 'Request Quote' : 'Yêu cầu báo giá'}
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-[#264f78] text-base font-medium rounded-md text-[#264f78] bg-white hover:bg-[#264f78] hover:text-white transition-all duration-300"
            >
              {language === 'en' ? 'Contact Us' : 'Liên hệ'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AllProducts;
